// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Turning findings into edits.
 *
 * ONE RULE OVERRIDES EVERYTHING ELSE IN THIS FILE: a finding whose severity is `expected` is never
 * edited. Not on the strict profile, not by a category override, not by anything. `expected` means
 * charsight positively identified a legitimate use — an emoji joiner, Persian ZWNJ, a registered
 * ideographic variation sequence, a balanced isolate pair — and removing those is not cleaning, it
 * is corrupting text in a language the user speaks and the tool does not.
 */

import type { Edit } from '../core/bytes';
import { deletion } from '../core/bytes';
import type { Encoding } from '../core/decode';
import { encodeString } from '../core/decode';
import type { Action, CategoryId, Finding } from './types';

export type Profile = 'report' | 'balanced' | 'strict';

export interface Policy {
  profile: Profile;
  /** Per-category user overrides. `expected` findings ignore these. */
  overrides: Partial<Record<CategoryId, Action>>;
  /**
   * Per-code-point overrides, which is what the ledger in the UI actually sets.
   *
   * The ledger groups by code point rather than by occurrence — one row reading
   * "U+200B ZERO WIDTH SPACE · 40 occurrences · [Remove]" instead of forty rows — and that row IS
   * the control. Keeping the policy keyed the same way the ledger is displayed means what you see
   * is exactly what gets applied, and the two cannot drift apart.
   */
  byCodePoint: Map<number, Action>;
}

export const DEFAULT_POLICY: Policy = {
  profile: 'balanced',
  overrides: {},
  byCodePoint: new Map(),
};

/**
 * Categories the strict profile will act on beyond the balanced default.
 *
 * Deliberately excludes:
 *  · `pua` — Nerd Fonts, Powerline and the Apple logo live there, and stripping them is the single
 *    change most likely to make a user think the tool is broken;
 *  · `homoglyph` — there is no safe automatic replacement. Folding Cyrillic а to Latin a would
 *    silently rewrite real Russian;
 *  · `combining` — a long mark run is worth seeing and is not automatically wrong;
 *  · `invalid-byte` — the bytes are passed through untouched, always. Anything else would break the
 *    byte-identical guarantee on exactly the files that need it most.
 */
const STRICT_ACTS_ON: ReadonlySet<CategoryId> = new Set<CategoryId>([
  'zero-width',
  'soft-hyphen',
  'whitespace',
  'control',
  'invisible-filler',
  'deprecated',
  'interlinear',
  'noncharacter',
  'line-separator',
  'typographic',
  'bidi',
]);

export function effectiveAction(f: Finding, policy: Policy): Action {
  if (f.severity === 'expected') return 'keep';

  const byCp = f.cp >= 0 ? policy.byCodePoint.get(f.cp) : undefined;
  if (byCp) {
    if (byCp === 'replace' && f.replacement === undefined) return 'remove';
    return byCp;
  }

  const override = policy.overrides[f.category];
  if (override) {
    if (override === 'replace' && f.replacement === undefined) return 'remove';
    return override;
  }

  if (policy.profile === 'report') return 'keep';
  if (policy.profile === 'balanced') return f.action;

  // strict
  if (f.action !== 'keep') return f.action;
  if (!STRICT_ACTS_ON.has(f.category)) return 'keep';
  return f.replacement !== undefined ? 'replace' : 'remove';
}

export interface Plan {
  edits: Edit[];
  /** Findings that will actually change a byte, in document order. */
  applied: Finding[];
  /**
   * Findings dropped because their span overlapped one already accepted. Reported rather than
   * silently discarded: a splicer that quietly skips a conflicting edit produces a file that looks
   * cleaned and is not.
   */
  skippedOverlaps: Finding[];
}

export function planEdits(
  findings: readonly Finding[],
  policy: Policy,
  encoding: Encoding,
): Plan {
  const wanted = findings
    .filter((f) => effectiveAction(f, policy) !== 'keep')
    .slice()
    .sort((a, b) => a.byteStart - b.byteStart || b.byteEnd - a.byteEnd);

  const edits: Edit[] = [];
  const applied: Finding[] = [];
  const skippedOverlaps: Finding[] = [];
  let prevEnd = -1;

  for (const f of wanted) {
    if (f.byteStart < prevEnd) {
      skippedOverlaps.push(f);
      continue;
    }
    const act = effectiveAction(f, policy);
    if (act === 'replace' && f.replacement !== undefined) {
      edits.push({
        start: f.byteStart,
        end: f.byteEnd,
        replacement: encodeString(f.replacement, encoding),
      });
    } else {
      edits.push(deletion(f.byteStart, f.byteEnd));
    }
    applied.push(f);
    prevEnd = f.byteEnd;
  }

  return { edits, applied, skippedOverlaps };
}
